import { useUser } from "@clerk/clerk-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { fetchClientImpactReport, fetchClientImpactReportJob } from "@/api/impactReport";
import { IMPACT_REPORT_JOB_POLL_INTERVAL_MS } from "@/constants/impactReport";
import type { ClientImpactReportJob, ReportFilter } from "@/interfaces/impactReport";
import {
  clearStoredImpactReportDeferral,
  impactReportQueryKey,
  isClientImpactReportDeferred,
  readStoredImpactReportDeferral,
  storeImpactReportDeferral,
} from "@/utils/impact-report-helper";

/**
 * The report is an expensive server-side aggregate, so it's held stale-but-usable for 5
 * minutes. The hero/card view toggle is a purely local switch over the already-fetched
 * `statements` registers (both ship on every metric) — it never touches `filter`, so it never
 * invalidates this query or triggers a refetch.
 *
 * A DEFERRED answer is never stale: each re-request starts a new background job (and a new
 * email), so a focus or remount must never re-issue it. Progress is read from the job instead.
 */
const STALE_TIME_MS = 5 * 60 * 1000;

/**
 * NO AUTOMATIC RETRIES — set explicitly rather than left at react-query's default of 3.
 *
 * The default MULTIPLIES: 4 browser attempts against an API that already retries transport
 * failures on its own side, turning one click into many runs of the aggregate — and it does that
 * hardest during an upstream slowdown, exactly when the upstream can least afford it.
 *
 * There is nothing left for a browser retry to win, because every failure reaching this hook is
 * already final when it arrives: a transport blip between Platform and Crunchy has spent its own
 * retry budget server-side; a 504 is Platform deliberately declining to re-run a slow aggregate
 * (see `services/impactReport/crunchyReportClient.ts` in the API); 4xx filter/auth rejections are
 * deterministic; a 429 would retry straight back into the rate limit.
 *
 * The one case a browser retry could genuinely fix is the user's own connection dropping — and
 * axios cannot cheaply tell "never reached Platform" (free to retry) from "reached Platform,
 * which is now holding an aggregate open for it" (expensive to retry), so guessing risks
 * re-running the very work this protects. Recovery there is the user's, via this hook's `refetch`
 * behind a visible "Try again" control.
 */
const RETRY_ATTEMPTS = false;

/**
 * `enabled` is how the page stays SELECTION-FIRST for GFG staff, and it defaults to `true` only so
 * the one existing signature keeps working.
 *
 * An organization user's empty filter is their whole organization, which the endpoint derives from
 * the caller. GFG staff have no single organization, so the endpoint answers 400 INVALID_FILTER to
 * their empty scope and there is no legal request until they select a campaign. Callers pass
 * `canRequestImpactReport(controls, { allowOrgScope })` here; a disabled query is idle, NOT
 * loading, and the page renders its pre-selection prompt rather than a spinner.
 *
 * Both call sites (the control bar, for the CAUSE options; the page, for the report) pass the
 * SAME memoised filter, so react-query hashes one key and the second call reads the first's cache
 * entry instead of issuing a second aggregate.
 */
export function useImpactReport(filter: ReportFilter, enabled = true) {
  // Scopes the sessionStorage deferral cache (below) to the SIGNED-IN user. `user?.id` is
  // `undefined` both while Clerk is still loading and while signed out; `?? null` collapses both
  // to the same "no known owner" value the storage helpers treat as "read and write nothing" —
  // without this, a second user signing in on the same tab with the same campaign selection would
  // be served the first user's deferral (their job id, their `recipient` email) before the query
  // ever reached the network.
  const { user, isLoaded } = useUser();
  const userId = isLoaded ? (user?.id ?? null) : null;

  // `/impact` is only ever reached under `App.tsx`'s `RequireAuth` (it returns `null` until its
  // own `useAuth().isLoaded` resolves, then redirects away when signed out) — so by the time this
  // hook's caller can render, SOME Clerk load has already completed. That is not the same load:
  // `useAuth()` and `useUser()` are two independent reads of Clerk's state, so this hook cannot
  // assume `useUser()`'s own `isLoaded` has already flipped just because `RequireAuth` rendered.
  // Gating the query on `isLoaded` here — not just on `userId !== null` — is what closes that
  // window: a request fired before `userId` resolves would store its 202 deferral (below) under
  // `userId === null`, i.e. drop it, so a later reload/return re-requests the same report,
  // starting a second upstream job and sending a second email.
  //
  // Exposed so the PAGE can also avoid drawing a wrong state (its pre-selection prompt, or an
  // error) for a query that is disabled ONLY because Clerk is still loading, not because the user
  // has no selection yet.
  const isAwaitingUser = !isLoaded;

  const query = useQuery({
    queryKey: impactReportQueryKey(filter),
    // A stored deferral (see `utils/impact-report-helper.ts`) is checked FIRST, before
    // any network call: react-query's in-memory cache is gone once `gcTime` has elapsed on an
    // unmounted query (the default 5 minutes), but a deferred answer must never be re-requested
    // after that — every re-request starts a brand-new upstream job and sends the recipient
    // ANOTHER email for the same report. sessionStorage survives both the gcTime eviction and a
    // full page reload, for as long as this filter's deferral stays within its own 7-day window.
    queryFn: async () => {
      const storedDeferral = readStoredImpactReportDeferral(userId, filter);
      if (storedDeferral) return storedDeferral;

      const response = await fetchClientImpactReport(filter);
      if (isClientImpactReportDeferred(response)) {
        storeImpactReportDeferral(userId, filter, response);
      }
      return response;
    },
    staleTime: (query) =>
      isClientImpactReportDeferred(query.state.data) ? Infinity : STALE_TIME_MS,
    retry: RETRY_ATTEMPTS,
    enabled: enabled && isLoaded && userId !== null,
  });

  return { ...query, isAwaitingUser };
}

/**
 * Forgets a filter's cached answer — for a deferral whose job failed, or whose job Crunchy has
 * since expired or lost (`REPORT_JOB_NOT_FOUND`). That deferral is never stale, so without this,
 * asking for the same selection again would replay the dead job rather than start a new one.
 *
 * REMOVING THE QUERY ALONE DOES NOT RE-FETCH IT — `queryClient.removeQueries()` clears the cache
 * entry (and, via `clearStoredImpactReportDeferral` below, the sessionStorage one), but an active
 * `QueryObserver` fires no new network call and no notification from the removal by itself
 * (verified against `@tanstack/query-core` 5.100.10). Reaching the network again needs an actual
 * trigger afterward:
 *   - "Start over" (`pages/ImpactReport.tsx`'s job-FAILED branch) gets one for free by also
 *     clearing the campaign selection in the same handler: the filter changes, so the next pick
 *     issues a fresh request against a key this discard already emptied.
 *   - "Build it again" (the job-NOT-FOUND branch, same file) keeps the SAME selection, so it has
 *     no filter change to ride on — it calls this, THEN an explicit `refetch()` on the still-active
 *     `useImpactReport` query, in the same click handler.
 *
 * Also clears the SAME user's, SAME filter's sessionStorage entry — see `utils/impact-report-
 * helper.ts` for why the entry is scoped to the signed-in user as well as the filter.
 */
export function useDiscardImpactReport() {
  const queryClient = useQueryClient();
  // Same `isLoaded` discipline as `useImpactReport` above: `user` is `undefined` while Clerk is
  // still resolving, which already collapses to `userId === null` below (and so is already a
  // no-op on the storage side) — but gating on `isLoaded` explicitly, rather than relying on that
  // coincidence, keeps every place in this hook family that reads the user id for storage doing
  // it the same, deliberate way.
  const { user, isLoaded } = useUser();
  const userId = isLoaded ? (user?.id ?? null) : null;
  return useCallback(
    (filter: ReportFilter) => {
      queryClient.removeQueries({ queryKey: impactReportQueryKey(filter), exact: true });
      clearStoredImpactReportDeferral(userId, filter);
    },
    [queryClient, userId],
  );
}

function isTerminalJob(job: ClientImpactReportJob | undefined): boolean {
  return job?.status === "completed" || job?.status === "failed";
}

/**
 * Polls a deferred report's job until it completes or fails. Polling stops on an error too — a
 * 404 is final (expired or not the caller's). `refetchIntervalInBackground` is left unset so a
 * hidden tab does not poll.
 *
 * A terminal job is never stale: after the link expires, a focus refetch would 404 and replace a
 * rendered report with the not-found state.
 */
export function useImpactReportJob(jobId: string | null) {
  return useQuery({
    queryKey: ["impactReportJob", jobId],
    queryFn: () => fetchClientImpactReportJob(jobId as string),
    enabled: !!jobId,
    retry: false,
    refetchInterval: (query) =>
      query.state.status === "error" || isTerminalJob(query.state.data)
        ? false
        : IMPACT_REPORT_JOB_POLL_INTERVAL_MS,
    staleTime: (query) => (isTerminalJob(query.state.data) ? Infinity : 0),
  });
}
